import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export const PASSWORD_MIN_LENGTH = 8;

/**
 * Password strength score 0-4 (mirrors the server's minimum-length rule; the
 * extra levels are guidance only).
 */
export function scorePassword(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= PASSWORD_MIN_LENGTH) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;
  return Math.min(score, 4);
}

const STRENGTH_COLORS = ['bg-red-400', 'bg-red-400', 'bg-yellow-400', 'bg-green-400', 'bg-green-600'];

interface PasswordInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  autoComplete: 'new-password' | 'current-password';
  disabled?: boolean;
  /** Show the strength meter + requirements hint (for password creation) */
  showStrength?: boolean;
  /** Rendered to the right of the label (e.g. a "Forgot password?" link) */
  labelAction?: React.ReactNode;
}

/**
 * PasswordInput - labeled password field with a show/hide toggle and an
 * optional strength meter. Shared by the auth pages.
 */
const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  value,
  onChange,
  label,
  autoComplete,
  disabled = false,
  showStrength = false,
  labelAction,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const strength = useMemo(() => scorePassword(value), [value]);
  const strengthLabels = [
    t('common:auth.passwordStrength.veryWeak', 'Very Weak'),
    t('common:auth.passwordStrength.weak', 'Weak'),
    t('common:auth.passwordStrength.fair', 'Fair'),
    t('common:auth.passwordStrength.good', 'Good'),
    t('common:auth.passwordStrength.strong', 'Strong'),
  ];

  return (
    <div>
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="form-label">
          {label}
        </label>
        {labelAction}
      </div>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          minLength={autoComplete === 'new-password' ? PASSWORD_MIN_LENGTH : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-base w-full pr-10"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          aria-label={
            visible
              ? t('common:auth.hidePassword', 'Hide password')
              : t('common:auth.showPassword', 'Show password')
          }
          tabIndex={-1}
        >
          {visible ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
        </button>
      </div>

      {showStrength && value.length > 0 && (
        <div className="mt-2">
          <div className="flex gap-1">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded ${i < strength ? STRENGTH_COLORS[strength] : 'bg-gray-200'}`}
              />
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {strengthLabels[strength]}
            {' · '}
            {t('common:auth.passwordRequirements', 'Use 8+ characters with a mix of letters, numbers & symbols')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
